/**
 * Font downloader — tipos puros, constantes y utilidades agnósticas de plataforma.
 *
 * Este módulo deliberadamente NO contiene lógica de descarga ni acceso a disco;
 * solo define el contrato `IFontDownloaderService`.
 *
 * @platform-implementation
 *   `apps/web/src/utils/font-downloader.ts` — único impl, sirve los 3 runtimes
 *   (navegador/PWA + Tauri desktop + Tauri móvil; todos cargan el bundle web):
 *     → FontFace API: `new FontFace(key, \`url("${url}")\`).load()` + `document.fonts`.
 *     → caché en memoria (`loadedFontKeys`); persistencia vía Cache Storage (pendiente).
 *     → puede limpiar las claves de una familia del registro de fuentes.
 */

import type { AbstractPowerSyncDatabase } from "@powersync/common";

// ── Tipos ─────────────────────────────────────────────────────────────────────

export const INGESTABLE_WEIGHTS = [
  "100",
  "200",
  "300",
  "400",
  "500",
  "600",
  "700",
  "800",
  "900",
] as const;
export type IngestableWeight = (typeof INGESTABLE_WEIGHTS)[number];

export type FontWeight = IngestableWeight;

export type FontCategory = "sans-serif" | "serif" | "display" | "handwriting" | "monospace";

export interface FontDownloaderVariant {
  weight: FontWeight;
  /** URL de descarga. Puede estar ausente si se resuelve vía attachment_id. */
  url?: string;
  /** ID del attachment en PowerSync para resolver la fuente desde almacenamiento local. */
  attachment_id?: string;
  id?: string;
  font_family_id?: string | null;
}

export interface CatalogFont {
  id: string;
  family: string;
  name: string;
  category: FontCategory;
}

/**
 * Contrato que la implementación debe cumplir.
 *
 * @platform-implementation `apps/web/src/utils/font-downloader.ts` → FontDownloaderService
 *   (único impl; sirve navegador/PWA + Tauri desktop + Tauri móvil).
 */
export interface IFontDownloaderService {
  /** Retorna true si la clave de fuente ya fue cargada en memoria. */
  isLoaded(fontKey: string): boolean;
  /** Elimina del registro en memoria (y de `document.fonts`) todas las variantes de una familia. */
  clearFamilyKeys(family: string): void;
  /**
   * Estado de caché persistente (Cache Storage / `document.fonts`).
   * Retorna "not-applicable" mientras la persistencia no esté implementada.
   */
  getDiskCacheStatus(
    family: string,
    variants: FontDownloaderVariant[],
  ): Promise<"cached" | "partial" | "not-cached" | "not-applicable">;
  /** Descarga y registra las variantes necesarias. */
  downloadAndLoad(
    family: string,
    variants: FontDownloaderVariant[],
    db?: AbstractPowerSyncDatabase,
  ): Promise<void>;
}

// ── Constantes ────────────────────────────────────────────────────────────────

export const FONT_CATEGORIES: readonly FontCategory[] = [
  "sans-serif",
  "serif",
  "display",
  "handwriting",
  "monospace",
] as const;

const FONT_WEIGHTS: ReadonlySet<string> = new Set([
  "100",
  "200",
  "300",
  "400",
  "500",
  "600",
  "700",
  "800",
  "900",
]);

const WEIGHT_NAMES: Record<FontWeight, string> = {
  "100": "Thin",
  "200": "ExtraLight",
  "300": "Light",
  "400": "Regular",
  "500": "Medium",
  "600": "SemiBold",
  "700": "Bold",
  "800": "ExtraBold",
  "900": "Black",
};

// ── Utilidades puras ──────────────────────────────────────────────────────────

export function isFontWeight(value: string | null | undefined): value is FontWeight {
  return typeof value === "string" && FONT_WEIGHTS.has(value);
}

/**
 * Genera la clave única de fuente para un par familia+peso.
 * Ejemplo: getFontKey("Open Sans", "400") → "OpenSans-Regular"
 */
export function getFontKey(family: string, weight: FontWeight): string {
  const safeFamily = family.replaceAll(/\s+/g, "");
  return `${safeFamily}-${WEIGHT_NAMES[weight]}`;
}
